import sift from "sift";

/**
 * Evaluates whether a record matches the given filter criteria.
 * Uses sift library which supports MongoDB-style query operators like $in, $gt, $lt, $elemMatch, etc.
 * Works directly with MongoDB-compatible filter syntax using dot notation for nested paths.
 *
 * @param record - The record to evaluate (kept as-is, no transformations applied)
 * @param filter - The filter criteria using dot notation for nested paths (supports MongoDB query operators including $elemMatch for arrays)
 * @returns true if the record matches all filter conditions, false otherwise
 *
 * @example
 * matchesFilter({ status: "CLOSED", amount: 100 }, { status: "CLOSED" })
 * // Returns: true
 *
 * @example
 * matchesFilter({ status: "OPEN" }, { status: "CLOSED" })
 * // Returns: false
 *
 * @example
 * matchesFilter({ status: "CLOSED" }, { status: { $in: ["CLOSED", "OPEN"] } })
 * // Returns: true (supports $in operator)
 *
 * @example
 * // Query arrays using $elemMatch with dot notation (recommended approach)
 * matchesFilter(
 *   { customer: { customFields: [{ key: "edi_invoice_selection", value: "both" }] } },
 *   { "customer.customFields": { $elemMatch: { key: "edi_invoice_selection", value: { $in: ["edi", "both"] } } } }
 * )
 * // Returns: true (supports $elemMatch for array queries)
 *
 * @example
 * matchesFilter({ status: "CLOSED" }, null)
 * // Returns: true (no filter means all records match)
 *
 * @example
 * matchesFilter({ status: "CLOSED" }, {})
 * // Returns: true (empty filter means all records match)
 */
export function matchesFilter(
  record: Record<string, any>,
  filter: Record<string, any> | null | undefined,
): boolean {
  // If no filter is specified, the operation is available for all records
  if (!filter || Object.keys(filter).length === 0) {
    return true;
  }

  // Use sift to evaluate the filter (supports MongoDB query operators)
  // sift requires dot notation for nested paths (e.g., "customer.customFields" instead of nested objects)
  // sift returns a function that can be used to test if a record matches
  const test = sift(filter);
  return test(record);
}
